import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { ClaimItem } from '../../api/types';
import { ClaimArtifacts } from '../../claimArtifactsSession';
import { formatPrice } from '../../money';

type PurchaseSummaryProps = {
  item: ClaimItem;
  orderNumber: string | null;
  artifacts: ClaimArtifacts | null;
};

type CopyState = 'idle' | 'copied' | 'failed';

function CodeBlock({ code, link }: { code: string; link: string | null }) {
  const [copyState, setCopyState] = useState<CopyState>('idle');

  const handleCopy = () => {
    if (!navigator.clipboard) {
      setCopyState('failed');
      return;
    }
    navigator.clipboard.writeText(code)
      .then(() => setCopyState('copied'))
      .catch(() => setCopyState('failed'));
  };

  return (
    <div className="external-sales-success__artifacts mt-4">
      <p className="text-muted mb-1">Your activation code</p>
      <div className="d-flex align-items-center justify-content-center">
        <code className="external-sales-success__code h4 mb-0">{code}</code>
        <Button variant="outline-secondary" size="sm" className="ml-2" onClick={handleCopy}>
          Copy code
        </Button>
      </div>
      <p className="mt-2 mb-0" role="status">
        {copyState === 'copied' && 'Copied.'}
        {copyState === 'failed' && 'Couldn’t copy — select the code and copy it manually.'}
      </p>
      {link && (
        <Button variant="primary" size="lg" block className="mt-3" href={link}>
          Open in the Dorian app
        </Button>
      )}
      <p className="text-muted mt-3 mb-0">We also sent this code to your email.</p>
    </div>
  );
}

export function PurchaseSummary({ item, orderNumber, artifacts }: PurchaseSummaryProps) {
  return (
    <div className="external-sales-success__summary">
      <h1 className="h4 mb-2">You’re all set!</h1>
      <p className="text-muted mb-4">Your purchase is confirmed.</p>
      <div className="d-flex justify-content-between align-items-center">
        <span className="external-sales-success__item-name">{item.name}</span>
        <span className="external-sales-success__item-price h5 mb-0">{formatPrice(item.price)}</span>
      </div>
      {orderNumber && (
        <>
          <p className="text-muted mt-3 mb-0">
            Order #
            {orderNumber}
          </p>
          <p className="text-muted mt-2 mb-0">
            <strong>Save your order number</strong>
            {' '}
            — take a screenshot or write it down.
          </p>
          <p className="text-muted mt-2 mb-0">
            Your activation code is on its way by email. If it hasn&rsquo;t arrived within
            an hour, contact support with this number and we&rsquo;ll help.
          </p>
        </>
      )}
      {artifacts === null && (
        <p className="text-muted mt-4 mb-0">
          Check your email for your access link and code — open it on your phone to redeem in the app.
        </p>
      )}
      {artifacts !== null && artifacts.code === null && (
        <p className="text-muted mt-4 mb-0">
          Your reward is on its way — check your email for the activation code.
        </p>
      )}
      {artifacts !== null && artifacts.code !== null && (
        <CodeBlock code={artifacts.code} link={artifacts.link} />
      )}
    </div>
  );
}
